/** @flow */
import { takeEvery, call, put, all } from "redux-saga/effects";
import http from "@http";
import {
  CreateMedicalTriage,
  AssignLifestyleChoices,
  GetPastMedicalHistory,
  UpdatePastMedicalHistory,
  DeletePastMedicalHistory,
  GetCurrentMedications,
  UpdateCurrentMedications,
  DeleteCurrentMedications,
  GetCurrentAllergies,
  UpdateCurrentAllergies,
  DeleteCurrentAllergies,
  CreateAssessmentOutcome,
  SubmitAssessmentOutcome,
  GetLifestyleAdvice,
  UpdateLifestyleAdvice,
  UpdateAdditionalSymptom,
  GetMedicalAssessment,
  UpdateSymptomAdvice,
  GetMedicalTriageClassification,
  GetMedicalTriageClassificationSchema,
  SubmitMedicalTriageClassification,
  UpdateMedicalTriage,
} from "../constants/actions";
import actionCreator from "../actionCreator";
import { parseSchema } from "@utils/schema";

function* onCreateMedicalTriage(action) {
  try {
    const { clientId, callInteractionId, data } = action.payload;
    const response = yield call(
      http.post,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage`,
      data,
    );

    yield put(actionCreator.CreateMedicalTriageSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.CreateMedicalTriageError(e.response));
  }
}

function* onUpdateMedicalTriage({ payload }) {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.callInteractionId ||
      !payload.medicalTriageId ||
      !payload.data
    ) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/medical-triage/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/medical-triage/${payload.medicalTriageId}`,
      payload.data,
    );

    yield put(actionCreator.UpdateMedicalTriageSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.UpdateMedicalTriageError(e.response));
  }
}

function* onAssignLifestyleChoices(action) {
  try {
    const { clientId, callInteractionId, medicalTriageId, data } = action.payload;

    const response = yield call(
      http.post,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/lifestyle-choices`,
      data,
    );

    yield put(actionCreator.AssignLifestyleChoicesSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.AssignLifestyleChoicesError(e.response));
  }
}

function* onGetPastMedicalHistory(action) {
  try {
    const { clientId, callInteractionId, medicalTriageId } = action.payload;
    const response = yield call(
      http.get,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/medical-history`,
    );

    yield put(actionCreator.GetPastMedicalHistorySuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetPastMedicalHistoryError(e.response));
  }
}

function* onDeletePastMedicalHistory(action) {
  try {
    const { clientId, callInteractionId, medicalTriageId, medicalHistoryId } = action.payload;

    const response = yield call(
      http.delete,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/medical-history/${medicalHistoryId}`,
    );
    yield put(actionCreator.DeletePastMedicalHistorySuccess(response.data));
  } catch (e) {
    yield put(actionCreator.DeletePastMedicalHistoryError(e.response));
  }
}

function* onUpdatePastMedicalHistory(action) {
  const { medicalHistory } = action.payload;
  const requests = [];
  const { clientId, callInteractionId, medicalTriageId } = action.payload;
  try {
    for (const row of medicalHistory) {
      if (!row.id) {
        requests.push(
          call(
            http.post,
            `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/medical-history`,
            row,
          ),
        );
      } else {
        requests.push(
          call(
            http.put,
            `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/medical-history/${row.id}`,
            row,
          ),
        );
      }
    }
    yield all(requests);
    yield put(actionCreator.UpdatePastMedicalHistorySuccess());
  } catch (e) {
    yield put(actionCreator.UpdatePastMedicalHistoryError(e.response));
  }
}

function* onGetCurrentAllergies(action) {
  try {
    const { clientId, callInteractionId, medicalTriageId } = action.payload;

    const response = yield call(
      http.get,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/allergy-details`,
    );

    yield put(actionCreator.GetCurrentAllergiesSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetCurrentAllergiesError(e.response));
  }
}

function* onDeleteCurrentAllergies(action) {
  try {
    const { clientId, callInteractionId, medicalTriageId, allergyId } = action.payload;

    const response = yield call(
      http.delete,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/allergy-details/${allergyId}`,
    );

    yield put(actionCreator.DeleteCurrentAllergiesSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.DeleteCurrentAllergiesError(e.response));
  }
}

function* onUpdateCurrentAllergies(action) {
  const { allergies } = action.payload;
  const requests = [];
  const { clientId, callInteractionId, medicalTriageId } = action.payload;
  try {
    for (const row of allergies) {
      if (!row.id) {
        requests.push(
          call(
            http.post,
            `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/allergy-details`,
            row,
          ),
        );
      } else {
        requests.push(
          call(
            http.put,
            `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/allergy-details/${row.id}`,
            row,
          ),
        );
      }
    }
    yield all(requests);
    yield put(actionCreator.UpdateCurrentAllergiesSuccess());
  } catch (e) {
    yield put(actionCreator.UpdateCurrentAllergiesError(e.response));
  }
}

function* onGetCurrentMedications(action) {
  try {
    const { clientId, callInteractionId, medicalTriageId } = action.payload;

    const response = yield call(
      http.get,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/medication-details`,
    );

    yield put(actionCreator.GetCurrentMedicationsSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetCurrentMedicationsError(e.response));
  }
}

function* onDeleteCurrentMedications(action) {
  try {
    const { clientId, callInteractionId, medicalTriageId, medicationId } = action.payload;

    const response = yield call(
      http.delete,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/medication-details/${medicationId}`,
    );

    yield put(actionCreator.DeleteCurrentMedicationsSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.DeleteCurrentMedicationsError(e.response));
  }
}

function* onUpdateCurrentMedications(action) {
  const { medication } = action.payload;
  const requests = [];
  const { clientId, callInteractionId, medicalTriageId } = action.payload;
  try {
    for (const row of medication) {
      if (!row.id) {
        requests.push(
          call(
            http.post,
            `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/medication-details`,
            row,
          ),
        );
      } else {
        requests.push(
          call(
            http.put,
            `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/medication-details/${row.id}`,
            row,
          ),
        );
      }
    }
    yield all(requests);
    yield put(actionCreator.UpdateCurrentMedicationsSuccess());
  } catch (e) {
    yield put(actionCreator.UpdateCurrentMedicationsError(e.response));
  }
}

function* onCreateAssessmentOutcome(action) {
  const { symptoms } = action.payload;
  const { clientId, callInteractionId, medicalTriageId } = action.payload;
  let response = null; // TODO very strange saga
  try {
    for (const row of symptoms) {
      const { data } = yield call(
        http.post,
        `/api/v2/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/symptom/${row.symptom.id}/question-answers/submit`,
        {
          answers: row.answers,
        },
      );
      response = data;
    }
    yield put(actionCreator.CreateAssessmentOutcomeSuccess(response));
  } catch (e) {
    yield put(actionCreator.CreateAssessmentOutcomeError(e.response));
  }
}

function* onSubmitAssessmentOutcome({ payload }) {
  const { clientId, callInteractionId, medicalTriageId, data } = payload;
  try {
    const response = yield call(
      http.post,
      `/api/v2/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/assessment-outcome/confirm`,
      data,
    );

    yield put(actionCreator.SubmitAssessmentOutcomeSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.SubmitAssessmentOutcomeError(e.response));
  }
}

function* onGetLifestyleAdvice(action) {
  try {
    const { clientId, callInteractionId, medicalTriageId } = action.payload;
    const response = yield call(
      http.get,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/lifestyle-advice`,
    );

    yield put(actionCreator.GetLifestyleAdviceSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetLifestyleAdviceError(e.response));
  }
}

function* onUpdateLifestyleAdvice({ payload }) {
  try {
    const { clientId, callInteractionId, medicalTriageId, lifestyleAdvices } = payload;
    yield call(
      http.post,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/lifestyle-advice/submit`,
      lifestyleAdvices,
    );

    yield put(actionCreator.UpdateLifestyleAdviceSuccess());
  } catch (e) {
    yield put(actionCreator.UpdateLifestyleAdviceError(e.response));
  }
}

function* onUpdateAdditionalSymptom({ payload }) {
  try {
    const { clientId, callInteractionId, medicalTriageId, additionalSymptoms } = payload;

    const response = yield call(
      http.post,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/symptom/additional/submit`,
      {
        additionalSymptoms,
      },
    );

    yield put(actionCreator.UpdateAdditionalSymptomSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.UpdateAdditionalSymptomError(e.response));
  }
}

function* onGetMedicalAssessment(action) {
  try {
    const { clientId, callInteractionId, medicalTriageId } = action.payload;

    const response = yield call(
      http.get,
      `/api/v2/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/medical-assessment`,
    );

    yield put(actionCreator.GetMedicalAssessmentSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetMedicalAssessmentError(e.response));
  }
}

function* onUpdateSymptomAdvice(action) {
  const { symptomAdvices } = action.payload;
  const { clientId, callInteractionId, medicalTriageId } = action.payload;
  let response = null;
  try {
    for (const row of symptomAdvices) {
      const { data } = yield call(
        http.post,
        `/api/v2/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/symptom/${row.symptomId}/advice/submit`,
        {
          adviceGiven: row.givenAdvices,
        },
      );

      response = data;
    }
    yield put(actionCreator.UpdateSymptomAdviceSuccess(response));
  } catch (e) {
    yield put(actionCreator.UpdateSymptomAdviceError(e.response));
  }
}

function* onGetMedicalTriageClassification({ payload }) {
  try {
    const { clientId, callInteractionId, medicalTriageId } = payload;

    const { data } = yield call(
      http.get,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/medical-triage-classification`,
    );

    delete data.hasConsentToShare;
    yield put(actionCreator.GetMedicalTriageClassificationSuccess(data));
  } catch (e) {
    yield put(
      actionCreator.GetMedicalTriageClassificationError(
        // Suppress valid 404 responses, there's no way to known whether a
        // medical classification has already been submitted before making this
        // request.  So there's no point in reporting this 404 error to the user.
        e.response.status === 404 ? undefined : e.response,
      ),
    );
  }
}

function* onGetMedicalTriageClassificationSchema({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw new Error("Invalid payload");
    }

    const schema = yield parseSchema("MedicalTriageClassificationDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreator.GetMedicalTriageClassificationSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreator.GetMedicalTriageClassificationSchemaError(e.response));
  }
}

function* onSubmitMedicalTriageClassification({ payload }) {
  try {
    const { clientId, callInteractionId, medicalTriageId, classification } = payload;
    const response = yield call(
      http.post,
      `/api/v1/medical-triage/client/${clientId}/call-interaction/${callInteractionId}/medical-triage/${medicalTriageId}/medical-triage-classification/submit`,
      classification,
    );

    yield all([
      put(actionCreator.GetCallInteraction({ clientId, callInteractionId })),
      put(actionCreator.SubmitMedicalTriageClassificationSuccess(response.data)),
    ]);
  } catch (e) {
    yield put(actionCreator.SubmitMedicalTriageClassificationError(e.response));
  }
}

export default function*(): Generator<*, *, *> {
  yield takeEvery(CreateMedicalTriage, onCreateMedicalTriage);
  yield takeEvery(UpdateMedicalTriage, onUpdateMedicalTriage);
  yield takeEvery(AssignLifestyleChoices, onAssignLifestyleChoices);

  yield takeEvery(GetPastMedicalHistory, onGetPastMedicalHistory);
  yield takeEvery(DeletePastMedicalHistory, onDeletePastMedicalHistory);
  yield takeEvery(UpdatePastMedicalHistory, onUpdatePastMedicalHistory);

  yield takeEvery(GetCurrentAllergies, onGetCurrentAllergies);
  yield takeEvery(DeleteCurrentAllergies, onDeleteCurrentAllergies);
  yield takeEvery(UpdateCurrentAllergies, onUpdateCurrentAllergies);

  yield takeEvery(GetCurrentMedications, onGetCurrentMedications);
  yield takeEvery(DeleteCurrentMedications, onDeleteCurrentMedications);
  yield takeEvery(UpdateCurrentMedications, onUpdateCurrentMedications);

  yield takeEvery(CreateAssessmentOutcome, onCreateAssessmentOutcome);
  yield takeEvery(SubmitAssessmentOutcome, onSubmitAssessmentOutcome);

  yield takeEvery(GetMedicalAssessment, onGetMedicalAssessment);
  yield takeEvery(GetLifestyleAdvice, onGetLifestyleAdvice);
  yield takeEvery(UpdateLifestyleAdvice, onUpdateLifestyleAdvice);
  yield takeEvery(UpdateAdditionalSymptom, onUpdateAdditionalSymptom);
  yield takeEvery(UpdateSymptomAdvice, onUpdateSymptomAdvice);

  yield takeEvery(GetMedicalTriageClassification, onGetMedicalTriageClassification);
  yield takeEvery(GetMedicalTriageClassificationSchema, onGetMedicalTriageClassificationSchema);
  yield takeEvery(SubmitMedicalTriageClassification, onSubmitMedicalTriageClassification);
}
